import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Wallet, Boxes, Tag, Percent, Store, Award, BarChart3, Layers, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { KpiCard } from '@/components/ui/KpiCard';
import { Select } from '@/components/form/Select';
import { formatINR } from '@/lib/format';
import { listSisStores } from '@/api/sisStock';
import { getSisAnalytics, type SisStoreRank, type SisSkuRank } from '@/api/sisAnalytics';

const money = (n: number) => `₹${formatINR(n)}`;
const compact = (n: number) =>
  Math.abs(n) >= 1_00_00_000 ? `₹${(n / 1_00_00_000).toFixed(2)}Cr`
  : Math.abs(n) >= 1_00_000 ? `₹${(n / 1_00_000).toFixed(2)}L`
  : Math.abs(n) >= 1_000 ? `₹${(n / 1_000).toFixed(1)}K`
  : `₹${Math.round(n)}`;
const pct = (v: number, total: number) => (total > 0 && v > 0 ? `${((v / total) * 100).toFixed(v / total < 0.1 ? 1 : 0)}%` : '0%');

// ERP theme colours. A group keeps its colour on every chart, whatever the filter.
const ACCENT = '#FF8C00';
const MUTED = '#94A3B8';
const GROUP_COLORS = ['#3B82F6', ACCENT, '#10B981', '#8B5CF6', '#EC4899', '#06B6D4'];

type Metric = 'netSales' | 'salesPerSqft' | 'units' | 'profitabilityPct';
const METRICS: { key: Metric; label: string; fmt: (n: number) => string }[] = [
  { key: 'netSales', label: 'Net sales', fmt: money },
  { key: 'salesPerSqft', label: 'Sales / sq ft', fmt: money },
  { key: 'units', label: 'Units', fmt: (n) => String(n) },
  { key: 'profitabilityPct', label: 'Profitability', fmt: (n) => `${n}%` },
];

export function SisAnalyticsPage() {
  // All / Date (from–to) / Month / Quarter / Year — opens on the current month.
  const period = usePeriod('month');
  const { range } = period;
  const [group, setGroup] = useState('');
  const [metric, setMetric] = useState<Metric>('netSales');
  const [hoverSeg, setHoverSeg] = useState<number | null>(null);

  const filter = useMemo(() => ({ from: range?.from, to: range?.to, group: group || undefined }), [range, group]);
  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const query = useQuery({ queryKey: ['sis-analytics', filter], queryFn: () => getSisAnalytics(filter) });
  const data = query.data;
  const k = data?.kpis;
  const loading = query.isFetching && !data;
  const stores = data?.byStore ?? [];

  const groups = useMemo(
    () => Array.from(new Set((storesQ.data ?? []).map((s) => s.group).filter(Boolean))).sort(),
    [storesQ.data],
  );
  const groupColor = (g: string) => { const i = groups.indexOf(g); return i >= 0 && i < GROUP_COLORS.length ? GROUP_COLORS[i] : MUTED; };
  const maxSpsf = useMemo(() => Math.max(1, ...stores.map((s) => s.salesPerSqft ?? 0)), [stores]);

  // Stores ranked by the chosen measure; a store with no area has no sales/sq ft and goes last.
  const m = METRICS.find((x) => x.key === metric)!;
  const ranked = useMemo(
    () => [...stores].sort((a, b) => (b[metric] ?? -Infinity) - (a[metric] ?? -Infinity)),
    [stores, metric],
  );
  const maxMetric = Math.max(1, ...stores.map((s) => s[metric] ?? 0));

  const byGroup = useMemo(() => {
    const map = new Map<string, { sales: number; stores: number }>();
    for (const s of stores) {
      const g = s.group || 'Ungrouped';
      const cur = map.get(g) ?? { sales: 0, stores: 0 };
      map.set(g, { sales: cur.sales + s.netSales, stores: cur.stores + 1 });
    }
    return [...map.entries()].map(([name, v]) => ({ name, ...v })).filter((g) => g.sales > 0).sort((a, b) => b.sales - a.sales);
  }, [stores]);
  const groupTotal = byGroup.reduce((a, g) => a + g.sales, 0);
  const hovered = hoverSeg == null ? undefined : byGroup[hoverSeg];

  // Categories that differ only by letter case ("Carpet" / "CARPET") are one category;
  // the list keeps the ten biggest and folds the rest into a single row.
  const cats = useMemo(() => {
    const map = new Map<string, { category: string; netSales: number; units: number; top: number }>();
    for (const c of data?.byCategory ?? []) {
      const key = (c.category || '').trim().toLowerCase();
      const cur = map.get(key);
      if (!cur) map.set(key, { category: c.category, netSales: c.netSales, units: c.units, top: c.netSales });
      else {
        if (c.netSales > cur.top) { cur.category = c.category; cur.top = c.netSales; }
        cur.netSales += c.netSales;
        cur.units += c.units;
      }
    }
    const all = [...map.values()].sort((a, b) => b.netSales - a.netSales);
    const rest = all.slice(10);
    return rest.length
      ? [...all.slice(0, 10), { category: `Other (${rest.length} categories)`, netSales: rest.reduce((a, c) => a + c.netSales, 0), units: rest.reduce((a, c) => a + c.units, 0), top: 0 }]
      : all;
  }, [data]);
  const catCount = new Set((data?.byCategory ?? []).map((c) => (c.category || '').trim().toLowerCase())).size;
  const catTotal = cats.reduce((a, c) => a + c.netSales, 0);
  const skuRows = (rows: SisSkuRank[]) => rows.map((r) => ({
    key: String(r.itemId), label: r.name || r.code,
    sub: [r.code, r.category, `${r.units} unit${r.units === 1 ? '' : 's'}`].filter(Boolean).join(' · '),
    value: r.netSales, display: money(r.netSales),
  }));

  return (
    <div>
      <SectionHeader title="SIS Analytics" />

      {/* KPIs */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard label="Total Sales" value={k ? money(k.totalSales) : '—'} accent="#8b5cf6" Icon={Wallet} loading={loading} />
        <KpiCard label="Units Sold" value={k ? k.totalUnits : '—'} accent="#3b82f6" Icon={Boxes} loading={loading} />
        <KpiCard label="Avg Sell Price" value={k ? money(k.avgSellPrice) : '—'} accent="#10b981" Icon={Tag} loading={loading} />
        <KpiCard label="Profitability" value={k ? `${k.profitabilityPct}%` : '—'} accent="#f59e0b" Icon={Percent} loading={loading} />
        <KpiCard label="Active Stores" value={k ? k.stores : '—'} accent="#6366f1" Icon={Store} loading={loading} />
        <KpiCard label="Top Store" value={k ? k.bestStore : '—'} accent="#ec4899" Icon={Award} size="sm" loading={loading} />
      </div>

      <FilterBar>
        <FilterField label="Group">
          <Select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">All Groups</option>
            {groups.map((g) => <option key={g}>{g}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
      </FilterBar>

      {/* Store comparison + group share */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card
          className="md:col-span-2"
          title="Store performance"
          icon={BarChart3}
          action={
            <div className="inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1">
              {METRICS.map((x) => (
                <button
                  key={x.key}
                  type="button"
                  onClick={() => setMetric(x.key)}
                  className={'rounded-md px-2 py-1 text-[11px] font-bold transition ' + (metric === x.key ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')}
                >
                  {x.label}
                </button>
              ))}
            </div>
          }
        >
          {loading ? <Skeleton /> : ranked.length === 0 ? <Empty text="No store sales in this period." /> : (
            <>
              <div className="space-y-1.5">
                {ranked.map((s) => {
                  const v = s[metric];
                  return (
                    <div key={s.partyId} className="flex items-center gap-3 text-xs" title={`${s.name} — ${money(s.netSales)} · ${s.units} units · ${s.salesPerSqft == null ? 'no area set' : `${money(s.salesPerSqft)}/sq ft`} · ${s.profitabilityPct}% profitability`}>
                      <span className="w-32 shrink-0 truncate font-bold text-[var(--text-primary)] sm:w-44">{s.name}</span>
                      <div className="h-2.5 min-w-0 flex-1 rounded-r-full bg-[var(--bg-soft)]">
                        {v != null && v > 0 && <div className="h-full rounded-r-full" style={{ width: `${(v / maxMetric) * 100}%`, background: groupColor(s.group) }} />}
                      </div>
                      <span className={'w-24 shrink-0 text-right font-bold ' + (v != null && v < 0 ? 'text-danger' : 'text-[var(--text-primary)]')}>
                        {v == null ? <span className="font-normal text-[var(--text-muted)]">no area</span> : m.fmt(v)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-[var(--border-color)] pt-2.5 text-[11px] text-[var(--text-secondary)]">
                {Array.from(new Set(ranked.map((s) => s.group || 'Ungrouped'))).sort().map((g) => (
                  <span key={g} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: groupColor(g) }} />{g}</span>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card title="Sales by group" icon={Store}>
          {loading ? <Skeleton /> : byGroup.length === 0 ? <Empty text="No sales in this period." /> : (
            <>
              <div className="relative mx-auto h-36 w-36" onMouseLeave={() => setHoverSeg(null)}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={byGroup} dataKey="sales" nameKey="name" innerRadius={46} outerRadius={66} paddingAngle={byGroup.length > 1 ? 2 : 0} stroke="none"
                      startAngle={90} endAngle={-270} isAnimationActive={false}
                      onMouseEnter={(_, i) => setHoverSeg(i)} onMouseLeave={() => setHoverSeg(null)}
                    >
                      {byGroup.map((g, i) => <Cell key={g.name} fill={groupColor(g.name)} fillOpacity={hoverSeg == null || hoverSeg === i ? 1 : 0.35} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {/* Centre readout: the total, or the hovered group. */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-extrabold text-[var(--text-primary)]">{compact(hovered ? hovered.sales : groupTotal)}</span>
                  <span className="max-w-[80px] truncate text-[9px] font-bold uppercase tracking-widest text-[var(--text-muted)]">{hovered ? hovered.name : 'Net sales'}</span>
                  {hovered && <span className="text-[10px] font-semibold text-[var(--text-secondary)]">{pct(hovered.sales, groupTotal)}</span>}
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {byGroup.map((g) => (
                  <div key={g.name} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex min-w-0 items-center gap-1.5 text-[var(--text-secondary)]">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: groupColor(g.name) }} />
                      <span className="truncate">{g.name}</span>
                      <span className="shrink-0 text-[10px] text-[var(--text-muted)]">{g.stores} store{g.stores === 1 ? '' : 's'}</span>
                    </span>
                    <span className="shrink-0 text-[var(--text-muted)]"><strong className="text-[var(--text-primary)]">{compact(g.sales)}</strong> · {pct(g.sales, groupTotal)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Store ranking — the full figures behind the chart */}
      <Table<SisStoreRank>
        storageKey="sis-analytics-stores"
        title="Store Ranking"
        count={`${stores.length} stores`}
        rows={stores}
        rowKey={(r) => r.partyId}
        loading={loading}
        columns={[
          { key: 'store', header: 'Store', cell: (r) => <span className="font-extrabold text-accent">{r.name}</span> },
          { key: 'group', header: 'Group', cell: (r) => (r.group ? <Badge tone="muted">{r.group}</Badge> : '—') },
          { key: 'area', header: 'Area (sq ft)', align: 'right', cell: (r) => (r.area || '—') },
          { key: 'sales', header: 'Net Sales', align: 'right', cell: (r) => money(r.netSales) },
          { key: 'units', header: 'Units', align: 'right', cell: (r) => r.units },
          {
            key: 'spsf',
            header: 'Sales / sq ft',
            align: 'right',
            cell: (r) =>
              r.salesPerSqft == null ? (
                <span className="text-[var(--text-muted)]">— (no area)</span>
              ) : (
                <div className="flex items-center justify-end gap-2">
                  <span className="h-1.5 rounded-full bg-accent/60" style={{ width: `${Math.max(4, (r.salesPerSqft / maxSpsf) * 60)}px` }} />
                  <strong className="text-accent">₹{formatINR(r.salesPerSqft)}</strong>
                </div>
              ),
          },
          {
            key: 'profit',
            header: 'Profitability',
            align: 'right',
            cell: (r) => (
              <strong className={r.profitabilityPct >= 0 ? 'text-success' : 'text-danger'}>{r.profitabilityPct}%</strong>
            ),
          },
        ]}
      />

      {/* What sells: top & bottom SKUs, and the category mix */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card title="Top SKUs" icon={Award} count={`${data?.topSkus.length ?? 0}`}>
          {loading ? <Skeleton /> : <BarList rows={skuRows(data?.topSkus ?? [])} color={ACCENT} empty="No SKU sales in this period." />}
        </Card>
        <Card title="Bottom SKUs" icon={Tag} count={`${data?.bottomSkus.length ?? 0}`}>
          {loading ? <Skeleton /> : <BarList rows={skuRows(data?.bottomSkus ?? [])} color={MUTED} bars={false} empty="No SKU sales in this period." />}
        </Card>
        <Card title="Sales by category" icon={Layers} count={`${catCount} categor${catCount === 1 ? 'y' : 'ies'}`}>
          {loading ? <Skeleton /> : (
            <BarList
              rows={cats.map((c) => ({ key: c.category, label: c.category || 'Uncategorised', sub: `${c.units} unit${c.units === 1 ? '' : 's'} · ${pct(c.netSales, catTotal)} of sales`, value: c.netSales, display: money(c.netSales) }))}
              color={ACCENT}
              empty="No category sales in this period."
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, count, action, className = '', children }: {
  title: string; icon: LucideIcon; count?: string; action?: ReactNode; className?: string; children: ReactNode;
}) {
  return (
    <div className={`overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-color)] px-4 py-2">
        <span className="flex items-center gap-2 text-sm font-extrabold text-[var(--text-primary)]"><Icon size={14} className="text-accent" /> {title}</span>
        {action ?? (count && <span className="text-xs text-[var(--text-muted)]">{count}</span>)}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/** Ranked list with an optional bar under each row — every value is labelled, so no tooltip is needed. */
function BarList({ rows, color, empty, bars = true }: { rows: { key: string; label: string; sub?: string; value: number; display: string }[]; color: string; empty: string; bars?: boolean }) {
  if (rows.length === 0) return <Empty text={empty} />;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className={bars ? 'space-y-2.5' : 'divide-y divide-[var(--border-color)] [&>div]:py-1.5'}>
      {rows.map((r) => (
        <div key={r.key} title={`${r.label} — ${r.display}`}>
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="truncate font-bold text-[var(--text-primary)]">{r.label}</span>
            <strong className="shrink-0 text-[var(--text-primary)]">{r.display}</strong>
          </div>
          {r.sub && <div className="truncate text-[10px] text-[var(--text-muted)]">{r.sub}</div>}
          {bars && (
            <div className="mt-1 h-1.5 rounded-full bg-[var(--bg-soft)]">
              {r.value > 0 && <div className="h-full rounded-full" style={{ width: `${Math.max(2, (r.value / max) * 100)}%`, background: color }} />}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Skeleton() {
  return <div className="h-40 animate-pulse rounded-lg" style={{ background: 'var(--border-color)' }} />;
}

function Empty({ text }: { text: string }) {
  return <div className="py-8 text-center text-xs text-[var(--text-muted)]">{text}</div>;
}
